// Handoff packages: create, view, share, download, and lists (requirement 7, 11.9b).
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@cloudscape-design/components/alert';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Link from '@cloudscape-design/components/link';
import Multiselect from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import { MAX_PACKAGE_SAMPLES, ROLES, type BdaSchema, type Recipient, type Role } from '@bda/shared';
import { api } from '../../api/client';
import { keys, useBlueprint, usePackage } from '../../api/hooks';
import { hasRole, useAuth } from '../../auth/AuthProvider';
import { SchemaView } from '../blueprints/BlueprintView';
import { formatDate } from '../../lib/format';
import { ErrorDetail, errorMessage, useNotify } from '../../lib/notify';

export function NewPackagePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const { user } = useAuth();
  const blueprintId = params.get('blueprintId') ?? '';
  const bp = useBlueprint(blueprintId);
  const isOwner = bp.data?.blueprint.ownerSub === user?.sub;
  const published = bp.data?.versions.map((v) => v.version) ?? [];
  const [version, setVersion] = useState<number | null>(Number(params.get('version')) || null);
  const [title, setTitle] = useState('');
  const [intendedUse, setIntendedUse] = useState('');
  const [contact, setContact] = useState(user?.email ?? '');
  const [samples, setSamples] = useState<string[]>(params.get('sample') ? [params.get('sample')!] : []);
  const [submitting, setSubmitting] = useState(false);
  const canPickSamples = isOwner && hasRole(user, 'BlueprintAuthor');
  const runs = useQuery({
    queryKey: ['runs', 'samples', blueprintId, version],
    queryFn: () => api.listRuns({ blueprintId, status: 'SUCCEEDED' }),
    enabled: canPickSamples && version !== null,
  });
  const sampleOptions = (runs.data?.items ?? [])
    .filter((r) => r.version === version && !r.expired)
    .map((r) => ({ value: r.id, label: r.fileName, description: formatDate(r.startedAt) }));

  if (bp.error)
    return (
      <Alert type="error">
        <ErrorDetail error={bp.error} />
      </Alert>
    );
  if (!bp.data) return <Spinner size="large" />;
  if (published.length === 0)
    return <Alert type="warning">Publish a version of this blueprint before creating a handoff package.</Alert>;

  const validVersion = version !== null && published.includes(version);
  const submit = async () => {
    if (!validVersion) return;
    setSubmitting(true);
    try {
      const pkg = await api.createPackage({
        blueprintId,
        version,
        title,
        intendedUse,
        contact,
        sampleRunIds: samples.filter((s) => sampleOptions.some((o) => o.value === s)),
      });
      notify.success('Handoff package created. It will be ready to download shortly.');
      navigate(`/packages/${pkg.id}`);
    } catch (e) {
      notify.error(errorMessage(e), e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={`From ${bp.data.blueprint.name}`}>
          Create handoff package
        </Header>
      }
    >
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              disabled={!validVersion || !title.trim() || !intendedUse.trim() || !contact.trim()}
              onClick={() => void submit()}
            >
              Create package
            </Button>
          </SpaceBetween>
        }
      >
        <Container>
          <SpaceBetween size="l">
            <FormField
              label="Published version"
              description="Packages are made from a published version, never the draft."
            >
              <Select
                selectedOption={validVersion ? { value: String(version), label: `Version ${version}` } : null}
                placeholder="Choose a version"
                options={published.map((v) => ({ value: String(v), label: `Version ${v}` }))}
                onChange={(e) => {
                  setVersion(Number(e.detail.selectedOption.value));
                  setSamples([]);
                }}
              />
            </FormField>
            <FormField label="Title">
              <Input value={title} onChange={(e) => setTitle(e.detail.value)} />
            </FormField>
            <FormField label="Intended use" description="What the receiving team will automate with this blueprint.">
              <Textarea value={intendedUse} onChange={(e) => setIntendedUse(e.detail.value)} />
            </FormField>
            <FormField label="Contact">
              <Input value={contact} onChange={(e) => setContact(e.detail.value)} />
            </FormField>
            {canPickSamples ? (
              <FormField
                label="Sample documents (optional)"
                description={`Successful runs of this version, up to ${MAX_PACKAGE_SAMPLES}.`}
              >
                <Multiselect
                  selectedOptions={sampleOptions.filter((o) => samples.includes(o.value))}
                  options={sampleOptions}
                  placeholder={version === null ? 'Choose a version first' : 'Choose runs'}
                  statusType={runs.isLoading ? 'loading' : 'finished'}
                  empty="No successful runs of this version"
                  onChange={(e) =>
                    setSamples(e.detail.selectedOptions.map((o) => o.value!).slice(0, MAX_PACKAGE_SAMPLES))
                  }
                />
              </FormField>
            ) : (
              <Box color="text-body-secondary">Sample documents can only be added by the blueprint owner.</Box>
            )}
          </SpaceBetween>
        </Container>
      </Form>
    </ContentLayout>
  );
}

const recipientText = (r: Recipient) => (r.type === 'USER' ? r.email : `Group: ${r.group}`);

function ShareManager({ packageId }: { packageId: string }) {
  const q = usePackage(packageId);
  const qc = useQueryClient();
  const notify = useNotify();
  const [text, setText] = useState('');
  const [group, setGroup] = useState<Role | null>(null);
  const users = useQuery({
    queryKey: ['directory', text],
    queryFn: () => api.directoryUsers(text),
    enabled: text.trim().length >= 2,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: keys.pkg(packageId) });
  const add = async (r: Recipient) => {
    try {
      await api.addShares(packageId, [r]);
      notify.success(`Shared with ${recipientText(r)}.`);
      setText('');
      await refresh();
    } catch (e) {
      notify.error(errorMessage(e), e);
    }
  };
  const revoke = async (key: string) => {
    try {
      await api.revokeShare(packageId, key);
      notify.success('Access removed.');
      await refresh();
    } catch (e) {
      notify.error(errorMessage(e), e);
    }
  };
  const shares = q.data?.shares ?? [];
  return (
    <Table
      header={
        <Header variant="h2" description="Recipients get read-only access and can download the package.">
          Sharing
        </Header>
      }
      items={shares}
      trackBy="recipientKey"
      empty={<Box>Not shared with anyone yet.</Box>}
      filter={
        <SpaceBetween direction="horizontal" size="m">
          <Autosuggest
            value={text}
            onChange={(e) => setText(e.detail.value)}
            onSelect={(e) => {
              const u = users.data?.items.find((x) => x.sub === e.detail.value);
              if (u) void add({ type: 'USER', sub: u.sub, email: u.email });
            }}
            options={(users.data?.items ?? []).map((u) => ({ value: u.sub, label: u.email, description: u.name }))}
            statusType={users.isFetching ? 'loading' : 'finished'}
            placeholder="Share with a person (type an email)"
            ariaLabel="Share with a person"
            enteredTextLabel={(v) => `Search "${v}"`}
            empty="No matching people"
          />
          <Select
            ariaLabel="Share with a group"
            placeholder="Share with a group"
            selectedOption={group ? { value: group, label: group } : null}
            options={ROLES.map((r) => ({ value: r, label: r }))}
            onChange={(e) => setGroup(e.detail.selectedOption.value as Role)}
          />
          <Button disabled={!group} onClick={() => group && void add({ type: 'GROUP', group })}>
            Share with group
          </Button>
        </SpaceBetween>
      }
      columnDefinitions={[
        { id: 'who', header: 'Recipient', cell: (s) => recipientText(s.recipient), isRowHeader: true },
        { id: 'when', header: 'Shared', cell: (s) => formatDate(s.grantedAt) },
        {
          id: 'revoke',
          header: 'Access',
          cell: (s) => (
            <Button
              variant="inline-link"
              ariaLabel={`Revoke ${recipientText(s.recipient)}`}
              onClick={() => void revoke(s.recipientKey)}
            >
              Revoke
            </Button>
          ),
        },
      ]}
    />
  );
}

export function PackagePage() {
  const { id } = useParams();
  const q = usePackage(id);
  const notify = useNotify();
  const qc = useQueryClient();
  if (q.error)
    return (
      <Alert type="error">
        <ErrorDetail error={q.error} />
      </Alert>
    );
  if (!q.data) return <Spinner size="large" />;
  const { package: p, canManage } = q.data;
  const download = async () => {
    try {
      window.location.assign((await api.downloadPackage(p.id)).url);
    } catch (e) {
      notify.error(errorMessage(e), e);
    }
  };
  const rebuild = async () => {
    try {
      await api.rebuildPackage(p.id);
      await qc.invalidateQueries({ queryKey: keys.pkg(p.id) });
    } catch (e) {
      notify.error(errorMessage(e), e);
    }
  };
  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`${p.blueprintName}, version ${p.version}`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {canManage && p.status === 'FAILED' && <Button onClick={() => void rebuild()}>Rebuild</Button>}
              <Button
                variant="primary"
                iconName="download"
                disabled={p.status !== 'READY'}
                onClick={() => void download()}
              >
                Download ZIP
              </Button>
            </SpaceBetween>
          }
        >
          {p.title}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {p.blueprintArchived && (
          <Alert type="info">The source blueprint has been archived. This package is still available.</Alert>
        )}
        <Container header={<Header variant="h2">Details</Header>}>
          <KeyValuePairs
            columns={3}
            items={[
              {
                label: 'Status',
                value:
                  p.status === 'READY' ? (
                    <StatusIndicator>Ready</StatusIndicator>
                  ) : p.status === 'BUILDING' ? (
                    <StatusIndicator type="loading">Building</StatusIndicator>
                  ) : (
                    <StatusIndicator type="error">Build failed</StatusIndicator>
                  ),
              },
              { label: 'Intended use', value: p.intendedUse },
              { label: 'Contact', value: p.contact },
              { label: 'Package author', value: p.authorEmail },
              { label: 'Blueprint owner', value: p.blueprintOwnerEmail },
              { label: 'Created', value: formatDate(p.createdAt) },
              { label: 'Blueprint ARN', value: <code>{p.blueprintArn}</code> },
              { label: 'Version', value: p.version },
              { label: 'BDA blueprint version', value: p.bdaVersion },
              { label: 'Samples', value: p.sampleRunIds.length },
            ]}
          />
        </Container>
        <Container header={<Header variant="h2">Schema</Header>}>
          <SchemaView schema={p.schema as BdaSchema} />
        </Container>
        {canManage && <ShareManager packageId={p.id} />}
      </SpaceBetween>
    </ContentLayout>
  );
}

function PackageList({ title, shared }: { title: string; shared: boolean }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: shared ? keys.shared : keys.myPackages,
    queryFn: () => (shared ? api.sharedPackages() : api.myPackages()),
  });
  return (
    <Table
      header={<Header variant="h1">{title}</Header>}
      loading={q.isLoading}
      items={q.data?.items ?? []}
      trackBy="id"
      empty={
        <Box textAlign="center">
          {shared ? 'Nothing has been shared with you yet.' : 'You have not created any handoff packages.'}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'title',
          header: 'Title',
          isRowHeader: true,
          cell: (p) => (
            <Link
              href={`/packages/${p.id}`}
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/packages/${p.id}`);
              }}
            >
              {p.title}
            </Link>
          ),
        },
        { id: 'bp', header: 'Blueprint', cell: (p) => `${p.blueprintName} v${p.version}` },
        { id: 'author', header: 'From', cell: (p) => p.authorEmail },
        {
          id: 'status',
          header: 'Status',
          cell: (p) =>
            p.blueprintArchived ? `${p.status.toLowerCase()} (blueprint archived)` : p.status.toLowerCase(),
        },
        { id: 'created', header: 'Created', cell: (p) => formatDate(p.createdAt) },
      ]}
    />
  );
}

export const MyPackagesPage = () => <PackageList title="My handoff packages" shared={false} />;
export const SharedPage = () => <PackageList title="Shared with me" shared />;
